import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { EmptyState, PageHeader, StatCard } from "../../components/Common";
import { RangeFilter, MiniChart } from "../../components/RangeFilter";
import { presetRange, rangeLabel, rangeParams, singleMonth } from "../../lib/range";
import { ReviewRanking } from "../../components/ReviewRanking";
import { api, formatIDR } from "../../lib/api";
import { CashCards, ProfitCard, VendorIncomeSection, useVendorSummary } from "../../components/VendorFinance";

function DriverRow({ r, month, i }) {
  const pct = r.total ? Math.round((r.paid / r.total) * 100) : 0;
  return (
    <Link
      to={`/admin/reports?driver=${r.driver_id}&month=${month}`}
      className="rise group grid grid-cols-2 md:grid-cols-[1.4fr_0.5fr_0.5fr_1fr_1fr_1fr_1fr_24px] items-center gap-3 px-5 py-4 border-t border-slate-100 hover:bg-emerald-50/40 transition-colors"
      style={{ animationDelay: `${i * 40}ms` }}
      data-testid={`driver-summary-${r.driver_id}`}
    >
      <div className="col-span-2 md:col-span-1">
        <p className="font-semibold text-slate-900">{r.driver_name}</p>
        <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} /></div>
      </div>
      <p className="text-sm"><span className="md:hidden text-slate-400">Trip </span><span className="font-num">{r.trips}</span></p>
      <p className="text-sm"><span className="md:hidden text-slate-400">Pax </span><span className="font-num">{r.pax}</span></p>
      <p className="font-num font-bold text-slate-900">{formatIDR(r.total)}</p>
      <p className="font-num text-emerald-700 text-sm">{formatIDR(r.paid)}</p>
      <p className="font-num text-amber-700 text-sm">{formatIDR(r.unpaid)}</p>
      <p className="text-sm"><span className="md:hidden text-slate-400">Susuk </span><span className="font-num text-slate-600">{formatIDR(r.susuk)}</span></p>
      <ArrowUpRight className="hidden md:block h-4 w-4 text-slate-300 group-hover:text-emerald-600 transition-colors" />
    </Link>
  );
}

export default function AdminDashboard() {
  const [range, setRange] = useState(() => presetRange("month"));
  const month = singleMonth(range) || (range.start || "").slice(0, 7);
  const [s, setS] = useState(null);
  const v = useVendorSummary(range);

  useEffect(() => {
    let live = true;
    api.get("/summary", { params: rangeParams(range) }).then((r) => { if (live) setS(r.data); });
    return () => { live = false; };
  }, [range]);

  return (
    <div>
      <PageHeader eyebrow="Ringkasan" title={rangeLabel(range)} />
      <RangeFilter value={range} onChange={setRange} />
      {s && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <div className="col-span-2"><StatCard tone="dark" label="Total Pendapatan Driver" value={formatIDR(s.total)} hint={`${s.trips} trip · ${s.pax} orang`} testId="admin-stat-total" /></div>
            <ProfitCard v={v} driverTotal={s.total} className="col-span-2 lg:col-start-1 lg:row-start-2" />
            <StatCard label="Order Baru" value={s.orders} testId="admin-stat-orders" delay={20} />
            <StatCard label="Collect (dari tamu)" value={formatIDR(s.collect)} testId="admin-stat-collect" delay={40} />
            <StatCard label="Sudah Dibayar" value={formatIDR(s.paid)} testId="admin-stat-paid" delay={80} />
            <StatCard label="Belum Dibayar" value={formatIDR(s.unpaid)} testId="admin-stat-unpaid" delay={160} />
            <StatCard label="Susuk (terpisah)" value={formatIDR(s.susuk)} testId="admin-stat-susuk" delay={240} />
            <CashCards v={v} paid={s.paid} unpaid={s.unpaid} />
          </div>
          <VendorIncomeSection v={v} />
          <MiniChart data={s.per_day} />
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Per Driver</h2>
          {s.per_driver.length ? (
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="driver-summary-table">
              <div className="hidden md:grid grid-cols-[1.4fr_0.5fr_0.5fr_1fr_1fr_1fr_1fr_24px] gap-3 px-5 py-3 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <span>Driver</span><span>Trip</span><span>Pax</span><span>Total</span><span>Dibayar</span><span>Belum</span><span>Susuk</span><span />
              </div>
              {s.per_driver.map((r, i) => <DriverRow key={r.driver_id} r={r} month={month} i={i} />)}
            </div>
          ) : (
            <EmptyState title="Belum ada report" text="Belum ada trip yang dilaporkan pada periode ini." testId="empty-summary" />
          )}
          <ReviewRanking start={range.start} end={range.end} />
        </>
      )}
    </div>
  );
}
